import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { Fragment, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { ThresholdStreakTaskCurrentThresholdState } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Ball } from '@sorare/core/src/atoms/icons/Ball';
import { iconChevronDown } from '@sorare/core/src/atoms/icons/system/iconChevronDown.ts';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  BrandS,
  BrandXS,
  BrandXXS,
  LabelL,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { TargetIcon } from '#src/assets/icons/TargetIcon.tsx';
import streakBackground from '#src/assets/streakBackground.webp';
import { CompactSo5Lineup } from '#src/components/lineup/CompactSo5Lineup/index.tsx';
import { Rewards } from '#src/components/missions/Rewards/index.tsx';
import { CurrentThresholdStreakHeader } from '#src/components/missions/Streaks/CurrentThresholdStreakHeader/index.tsx';
import { StreakProgressBar } from '#src/components/missions/Streaks/StreakProgressBar/index.tsx';
import { TaskCampaignLabel } from '#src/components/missions/Streaks/TaskCampaignLabel/index.tsx';
import { TaskCampaignText } from '#src/components/missions/Streaks/TaskCampaignText/index.tsx';
import { TutoRewards } from '#src/components/missions/Streaks/TutoRewards/index.tsx';

import type { DetailsDialog_thresholdStreakTask } from './__generated__/index.graphql.ts';
import fire from './assets/fire.svg';

const Content = styled(Vertical)`
  height: 100%;
  padding: 0 var(--triple-unit) var(--triple-unit);
`;
const Lineups = styled(Vertical)`
  width: 100%;
`;
const Streak = styled.span`
  background: var(--c-gradient-rewards-orange);
  background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
`;
const RelativeWrapper = styled.div`
  position: relative;
`;
const TutoRewardsWrapper = styled(Vertical)`
  overflow: hidden;
  position: relative;
  align-items: stretch;
  background-color: var(--c-black);
  padding: var(--triple-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
`;
const RewardsWrapper = styled(SBHorizontal)`
  width: 100%;
`;
const CornerBadgesWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  position: absolute;
  left: calc(-1 * var(--half-unit));
  top: 0;
  z-index: 1;
  align-items: flex-start;
`;

const OneShotTag = styled(Horizontal)`
  color: var(--c-black);
  background: linear-gradient(323deg, #fcff63 14.3%, #c26900 85.7%);
  padding: 2px var(--unit);
  border-radius: var(--unit);
`;

// Quick fix for the one shot campaign. Ideally, we should have a boolean from the backend
const ONE_SHOT_CAMPAIGN_SLUGS = [
  'boosted_rewards_5x',
  'boosted_rewards_5x_germany',
];

type Props = {
  thresholdStreakTask: DetailsDialog_thresholdStreakTask;
  onClose: () => void;
  showLive?: boolean;
};

const Root = styled(Vertical).attrs({ gap: 3 })`
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  backdrop-filter: blur(32px);
  --c-bar-background: var(--c-nd-50);

  &.reached {
    border: 1px solid #fef314;
    box-shadow:
      0 0 4px 0 rgba(254, 193, 24, 0.25) inset,
      0 0 8px 0 rgba(255, 78, 34, 0.5);
    backdrop-filter: blur(32px);
    background: url(${streakBackground}) center/cover no-repeat;
  }
`;

const Sup = styled(BrandXXS).attrs({ as: 'sup' })`
  vertical-align: top;
`;

const StyledIconButton = styled(IconButton)`
  transition: transform 0.3s ease-in-out;
`;

const StreakLineup = ({
  lineup,
  task,
}: {
  lineup: NonNullable<
    DetailsDialog_thresholdStreakTask['so5LeagueTrack']
  >['so5Leaderboards'][number]['mySo5Lineups'][number];
  task: DetailsDialog_thresholdStreakTask;
}) => {
  const [open, setOpen] = useState(false);
  const { currentThreshold, completedBy } = task;
  const { managerTeam, name, so5Rankings } = lineup;

  const currentScore = so5Rankings.at(0)?.score || 0;

  return (
    <Root
      className={classNames({
        reached:
          currentScore >= currentThreshold.score &&
          completedBy?.id === lineup.id,
      })}
    >
      <Vertical gap={0}>
        <ButtonBase onClick={() => setOpen(prev => !prev)}>
          <SBHorizontal>
            {managerTeam?.name || name}{' '}
            <Horizontal>
              <BrandS>
                <FormattedMessage
                  {...fantasy.ordinalPoints}
                  values={{
                    score: (
                      <FormattedNumber
                        value={currentScore}
                        maximumFractionDigits={2}
                      />
                    ),
                    sup: c => <Sup color="var(--c-nd-600)">{c}</Sup>,
                  }}
                />
              </BrandS>
              <StyledIconButton
                asSpan
                icon={iconChevronDown}
                size="small"
                color="transparent"
                style={{ transform: open ? 'rotate(-180deg)' : 'rotate(0deg)' }}
              />
            </Horizontal>
          </SBHorizontal>
        </ButtonBase>
        <Collapse gap={3} open={open}>
          <CompactSo5Lineup so5Lineup={lineup} />
        </Collapse>
      </Vertical>
      <StreakProgressBar
        currentThresholdScore={currentThreshold.score}
        lineup={lineup}
      />
    </Root>
  );
};

export const DetailsDialog = ({
  thresholdStreakTask,
  showLive = true,
  onClose,
}: Props) => {
  const {
    title,
    progress,
    currentThresholdStatus,
    currentThreshold,
    rarity,
    so5LeagueTrack,
    thresholds,
    scoreTaskCampaign,
    rewardTaskCampaign,
  } = thresholdStreakTask;

  const [displayIndex, setDisplayIndex] = useState<number>(
    Math.min(progress, thresholds.length - 1)
  );

  const displayedThreshold = thresholds[displayIndex];

  const lineups =
    so5LeagueTrack?.so5Leaderboards.flatMap(
      ({ mySo5Lineups }) => mySo5Lineups
    ) || [];
  const isLive =
    [
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_IN_PROGRESS,
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_HIT,
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_MISSED,
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_NOT_SET,
      ThresholdStreakTaskCurrentThresholdState.COMPLETED,
    ].includesValue(currentThresholdStatus.state) &&
    lineups.length > 0 &&
    showLive;

  let displayedThresholdIcon: ReactNode;
  if (
    displayedThreshold.score === currentThreshold.score &&
    currentThresholdStatus.state ===
      ThresholdStreakTaskCurrentThresholdState.LIVE_LINEUP_HIT
  ) {
    displayedThresholdIcon = <img src={fire} alt="" width="16" height="16" />;
  } else if (displayedThreshold.score <= currentThreshold.score) {
    displayedThresholdIcon = <TargetIcon color="var(--c-rewards-orange)" />;
  } else {
    displayedThresholdIcon = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <g opacity="0.5">
          <path
            d="M3.21338 7.2301H12.8536V14.4603H3.21338V7.2301Z"
            stroke="white"
            strokeWidth="1.60671"
          />
          <path
            d="M11.2471 7.23028V4.82022C11.2471 3.04551 9.80844 1.60681 8.03372 1.60681V1.60681C6.25901 1.60681 4.82031 3.0455 4.82031 4.82022V7.23028"
            stroke="white"
            strokeWidth="1.60671"
          />
          <path
            d="M6.42627 10.8452H9.63968"
            stroke="white"
            strokeWidth="1.60671"
          />
        </g>
      </svg>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={
        <LabelL bold className="text-center">
          {title}
        </LabelL>
      }
    >
      <Content gap={3}>
        <CurrentThresholdStreakHeader progress={progress} />
        {isLive ? (
          <Lineups gap={2}>
            {lineups?.map(lineup => {
              return (
                <StreakLineup
                  key={lineup.id}
                  lineup={lineup}
                  task={thresholdStreakTask}
                />
              );
            })}
          </Lineups>
        ) : (
          <RelativeWrapper>
            <CornerBadgesWrapper>
              {[scoreTaskCampaign, rewardTaskCampaign].map(
                taskCampaign =>
                  taskCampaign && (
                    <TaskCampaignLabel
                      key={taskCampaign.slug}
                      taskCampaign={taskCampaign}
                    />
                  )
              )}
            </CornerBadgesWrapper>
            <TutoRewardsWrapper className="text-center" center gap={3}>
              <TutoRewards
                thresholdStreakTask={thresholdStreakTask}
                displayIndex={displayIndex}
                setDisplayIndex={setDisplayIndex}
              />
              <BodyM bold>
                <FormattedMessage
                  defaultMessage="Your <bold>streak</bold> counts how many Game Weeks in a row you've hit the target."
                  values={{
                    bold: c => <Streak>{c}</Streak>,
                  }}
                />
              </BodyM>
            </TutoRewardsWrapper>
          </RelativeWrapper>
        )}
        <RewardsWrapper>
          <Horizontal gap={0.5}>
            {displayedThresholdIcon}
            <LabelL bold>
              <FormattedMessage defaultMessage="Streak Reward" />
            </LabelL>
          </Horizontal>
          <Horizontal>
            <Rewards
              rewardConfigs={displayedThreshold.rewardConfigs}
              rarity={rarity}
            />
          </Horizontal>
        </RewardsWrapper>
        <Vertical gap={1.5} center>
          {[rewardTaskCampaign, scoreTaskCampaign]
            .filter(Boolean)
            .some(({ slug }) => ONE_SHOT_CAMPAIGN_SLUGS.includes(slug)) ? (
            <>
              <OneShotTag as={BrandXS} gap={0.5}>
                <Ball size={1.5} />
                <FormattedMessage defaultMessage="One shot" />
              </OneShotTag>
              <BodyS className="text-center" color="var(--c-nd-600)">
                <FormattedMessage defaultMessage="Start your streak to enjoy 5x boosted rewards at every streak milestone. Streaks reset if you miss the score or after finishing the last level." />
              </BodyS>
            </>
          ) : (
            <BodyS>
              <FormattedMessage defaultMessage="Streaks reset if you miss the score or after finishing the last level." />
            </BodyS>
          )}

          <Vertical gap={0} center>
            {[rewardTaskCampaign, scoreTaskCampaign].map(taskCampaign => {
              if (!taskCampaign) return null;

              return (
                <Fragment key={taskCampaign.slug}>
                  <TaskCampaignText
                    key={taskCampaign.slug}
                    taskCampaign={taskCampaign}
                  />
                </Fragment>
              );
            })}
          </Vertical>
        </Vertical>
      </Content>
    </Dialog>
  );
};

DetailsDialog.fragments = {
  thresholdStreakTask: gql`
    fragment DetailsDialog_thresholdStreakTask on ThresholdsStreakTask {
      id
      title
      progress
      rarity
      completedBy {
        id
      }
      rewardTaskCampaign {
        slug
        ...TaskCampaignLabel_taskCampaign
        ...TaskCampaignText_taskCampaign
      }
      scoreTaskCampaign {
        slug
        ...TaskCampaignLabel_taskCampaign
        ...TaskCampaignText_taskCampaign
      }
      currentThresholdStatus {
        state
      }
      currentThreshold {
        score
      }
      thresholds {
        score
        rewardConfigs {
          id
          ...Rewards_anyRewardConfig
        }
      }
      so5LeagueTrack {
        slug
        so5Leaderboards {
          slug
          mySo5Lineups {
            id
            name
            managerTeam {
              id
              name
            }
            so5Rankings {
              id
              score
            }
            ...CompactSo5Lineup_so5Lineup
            ...StreakProgressBar_so5Lineup
          }
        }
      }
      ...TutoRewards_thresholdStreakTask
    }
    ${TutoRewards.fragments.thresholdStreakTask}
    ${Rewards.fragments.anyRewardConfig}
    ${TaskCampaignLabel.fragments.taskCampaign}
    ${TaskCampaignText.fragments.taskCampaign}
    ${CompactSo5Lineup.fragments.so5Lineup}
    ${StreakProgressBar.fragments.so5Lineup}
  ` as TypedDocumentNode<DetailsDialog_thresholdStreakTask>,
};
